<template>
    <Toast />
    <ConfirmDialog />
    <router-view v-slot="{ Component }">
        <Transition name="fade" mode="out-in">
            <component :is="Component" />
        </Transition>
    </router-view>
</template>

<script setup lang="ts">
import Toast from 'primevue/toast';
import ConfirmDialog from 'primevue/confirmdialog';
</script>
<style lang="scss">
*,
*::before,
*::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
}

body {
    background-color: $main-background-color;
    font-family: $font-family-archivo, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
}

body.p-overflow-hidden {
    padding-right: 0 !important;
}

html {
    scrollbar-gutter: stable !important;
    background-color: $main-background-color;
}

#app {
    min-height: 100vh;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

a {
    text-decoration: none;
    color: inherit;
}

img {
    display: block;
    max-width: 100%;
}

// ── ConfirmDialog
.p-confirmdialog {
    --p-dialog-border-radius: 5px;
    --p-dialog-shadow: 0 0 30px rgba($black-color, 0.1);
    --p-dialog-border-color: #{$border-gray-color};
    border: 1px solid $border-gray-color;
    border-radius: 5px;
    min-width: 420px;
    max-width: 500px;
    font-family: $font-family-archivo, sans-serif;

    .p-dialog-header {
        padding: 25px 30px 0;
        border-bottom: none;
        background: $white-color;
        border-radius: 5px 5px 0 0;

        .p-dialog-title {
            font-family: $font-family-archivo, sans-serif;
            font-weight: 500;
            font-size: 20px;
            line-height: 22px;
            color: $black-color;
        }

        .p-dialog-header-actions .p-dialog-close-button {
            color: $main-orange;
            box-shadow: none !important;
            background: transparent !important;
            border: none !important;
            width: 24px;
            height: 24px;

            &:hover {
                background: transparent !important;
                color: $main-orange-hover;
            }

            .p-icon {
                width: 16px;
                height: 16px;
            }
        }
    }

    .p-dialog-content {
        padding: 20px 30px 10px;
        background: $white-color;

        .p-confirmdialog-message-container {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .p-confirmdialog-icon {
            color: $main-orange;
            font-size: 22px;
            height: 100%;
            width: fit-content;
            flex-shrink: 0;
        }

        .p-confirmdialog-message {
            font-family: $font-family-archivo, sans-serif;
            font-weight: 400;
            font-size: 15px;
            line-height: 21px;
            color: rgba($black-color, 0.75);
        }
    }

    .p-dialog-footer {
        padding: 10px 30px 25px;
        background: $white-color;
        border-top: none;
        border-radius: 0 0 5px 5px;
        display: flex;
        justify-content: flex-end;
        gap: 10px;

        // shared button base
        .p-button {
            height: 46px !important;
            border-radius: 10px !important;
            font-family: $font-family-archivo, sans-serif !important;
            font-weight: 600 !important;
            font-size: 16px !important;
            line-height: 17px !important;
            box-shadow: none !important;
            padding: 0 22px !important;
        }

        // reject/cancel button
        .p-confirmdialog-reject-button {
            background: $white-color !important;
            border: 1px solid $border-gray-color !important;
            color: rgba($black-color, 0.7) !important;
            font-weight: 400 !important;

            &:hover {
                background: darken($white-color, 3%) !important;
            }
        }

        // accept/confirm button
        .p-confirmdialog-accept-button {
            background: $main-orange !important;
            border: 1px solid $main-orange !important;
            color: $white-color !important;

            &:hover {
                background: $main-orange-hover !important;
                border-color: $main-orange-hover !important;
            }
        }
    }
}

.p-tooltip.tooltip-app {
    filter: drop-shadow(0 0 1px $border-gray-color) drop-shadow(0 4px 16px rgba($black-color, 0.08));

    .p-tooltip-text {
        background: $white-color;
        color: rgba($black-color, 0.75);
        font-family: $font-family-archivo, sans-serif;
        font-size: 12px;
        font-weight: 500;
        padding: 5px 12px;
        border-radius: 6px;
    }

    &.p-tooltip-top .p-tooltip-arrow    { border-top-color: $white-color; }
    &.p-tooltip-bottom .p-tooltip-arrow { border-bottom-color: $white-color; }
    &.p-tooltip-left .p-tooltip-arrow   { border-left-color: $white-color; }
    &.p-tooltip-right .p-tooltip-arrow  { border-right-color: $white-color; }
}
</style>
